import { t } from 'i18next';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import { ISankeyNodeConfig, ISankeyNodeData } from '~/src/dash/components/charts/Sankey/common';
import { Col, Cols, Row, Rows, spacing, Span, Text } from '~/src/dash/publicaUI';
import { auctionInsightsKeyToTKeyMap } from '~/src/dash/utils/bidErrorCodes';
import { publicaColors } from '~/src/dash/utils/colors';
import { TKey } from '~/src/dash/publicaUI';

export const formatNodeTooltip = (
  nodeName: string,
  value: unknown,
  nodeConfigMap: Record<string, ISankeyNodeConfig>,
  nodeData?: ISankeyNodeData,
): string => {
  const nodeConfig = nodeConfigMap[nodeName];
  // Use original value from nodeData if available, otherwise use the passed value
  const originalValue = nodeData?.total ?? value;
  const formattedValue =
    typeof originalValue === 'number' ? originalValue.toLocaleString() : t('NotApplicable');
  const nodeColor = nodeConfig?.color || publicaColors.veryDarkGray;

  const individualTerminalValues: { tKey: TKey; value: number }[] = [];
  if (nodeData?.values) {
    Object.entries(nodeData.values).forEach(([key, value]) => {
      if (typeof value === 'number' && value > 0) {
        individualTerminalValues.push({
          tKey: auctionInsightsKeyToTKeyMap[key],
          value,
        });
      }
    });
    individualTerminalValues.sort((a, b) => b.value - a.value);
  }

  const hasIndividualTerminalValues = individualTerminalValues.length > 0;
  const columnCount = hasIndividualTerminalValues ? 3 : 2;
  const gridTemplateColumns = hasIndividualTerminalValues ? '1fr auto auto' : '1fr auto';

  return ReactDOMServer.renderToString(
    <div
      style={{
        display: 'grid',
        gridTemplateColumns,
        gap: `2px ${spacing.base}`,
        padding: spacing.extraSmall,
      }}
    >
      {/* Header row */}
      <div>
        <Text small bold style={{ whiteSpace: 'nowrap' }} tKey={nodeConfig?.tKey} />
      </div>
      <div style={{ textAlign: 'right' }}>
        <Text small bold style={{ whiteSpace: 'nowrap' }}>
          {formattedValue}
        </Text>
      </div>
      {hasIndividualTerminalValues && (
        <div style={{ textAlign: 'right' }}>
          <Text small bold style={{ whiteSpace: 'nowrap' }}>
            %
          </Text>
        </div>
      )}

      {/* Header underline - spans all columns */}
      <div
        style={{
          gridColumn: `1 / ${columnCount + 1}`,
          borderBottom: `1px solid ${nodeColor}`,
          height: '1px',
          marginTop: '-2px',
          marginBottom: '-2px',
        }}
      />

      {/* Data rows */}
      {hasIndividualTerminalValues &&
        individualTerminalValues.map((terminalValue) => (
          <React.Fragment key={terminalValue.tKey}>
            <div>
              <Text small style={{ whiteSpace: 'nowrap' }} tKey={terminalValue?.tKey} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text small style={{ whiteSpace: 'nowrap' }}>
                {terminalValue.value.toLocaleString()}
              </Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text small style={{ whiteSpace: 'nowrap' }}>
                {typeof originalValue === 'number' && originalValue > 0
                  ? `${((terminalValue.value / originalValue) * 100).toFixed(2)}%`
                  : 'N/A'}
              </Text>
            </div>
          </React.Fragment>
        ))}

      {/* Tooltip description */}
      {nodeConfig?.tooltipTKey && (
        <div
          style={{
            gridColumn: `1 / ${columnCount + 1}`,
            paddingTop: spacing.extraSmall,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          <Text small tKey={nodeConfig?.tooltipTKey} />
        </div>
      )}
    </div>,
  );
};

export const formatLinkTooltip = (
  source: string,
  target: string,
  nodeConfigMap: Record<string, ISankeyNodeConfig>,
  value: number,
): string => {
  const sourceTKey = nodeConfigMap[source]?.tKey;
  const targetTKey = nodeConfigMap[target]?.tKey;
  const formattedValue = value.toLocaleString();

  return ReactDOMServer.renderToString(
    <Rows smallPadding>
      <Row noPadding>
        <Cols
          gap={spacing.extraSmall}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            width: 'max-content',
            minWidth: '100%',
          }}
        >
          <Col style={{ flex: '0 0 auto', paddingRight: '16px' }}>
            <Text small bold style={{ whiteSpace: 'nowrap' }}>
              <Span bold tKey={sourceTKey} /> → <Span bold tKey={targetTKey} />
            </Text>
          </Col>
          <Col style={{ flex: '0 0 auto', textAlign: 'right' }}>
            <Text small style={{ whiteSpace: 'nowrap' }}>
              {formattedValue}
            </Text>
          </Col>
        </Cols>
      </Row>
    </Rows>,
  );
};
